import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessIcon from "@mui/icons-material/Business";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChatIcon from "@mui/icons-material/Chat";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TemplateIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { Box, IconButton, styled, Tooltip } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import React, { useEffect, useState } from "react";
import { useAccount, useSidebar } from "../../../context";
import {
  SidebarButtonProps,
  SidebarNavButtonProps,
} from "../../../interfaces/all";
import { getSsoToken } from "../../../services/api";

const Container = styled(Box)`
  width: 64px;
  background: #f0f2f5;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 0;
  gap: 6px;
  position: relative;
  height: 100vh; /* Altura fixa para desktop */
  max-height: 100vh; /* Limitar altura máxima */
  overflow-y: auto; /* Permitir scroll quando necessário */
  overflow-x: hidden;

  /* Scrollbar customizada */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 600px) {
    width: 50px;
    padding: 8px 0; /* Aumentar padding para melhor espaçamento */
    gap: 8px; /* Aumentar gap para evitar toques acidentais */
    /* Garante que a sidebar seja visível no mobile */
    z-index: 999;
    height: 100vh;
    height: -webkit-fill-available;
    height: 100dvh;
    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: 100dvh;

    /* Scrollbar mais fina em mobile */
    &::-webkit-scrollbar {
      width: 2px;
    }
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media (max-width: 600px) {
      height: 100dvh;
      max-height: 100dvh;
    }
  }
`;

const StyledIconButton = styled(IconButton)<SidebarButtonProps>`
  width: 48px;
  height: 48px;
  color: ${(props) => (props["data-active"] ? "#008069" : "#54656f")};
  background: ${(props) => (props["data-active"] ? "#e9edef" : "transparent")};
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props["data-active"] ? "#e9edef" : "rgba(0, 0, 0, 0.05)"};
    transform: scale(1.05);
  }

  & svg {
    font-size: 24px;
  }

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;

    & svg {
      font-size: 24px;
    }
  }
`;

const SidebarNavButton: React.FC<SidebarNavButtonProps> = ({
  icon: Icon,
  tooltip,
  active,
  onClick,
}) => (
  <Tooltip title={tooltip} placement="right">
    <StyledIconButton onClick={onClick} data-active={active}>
      <Icon />
    </StyledIconButton>
  </Tooltip>
);

const LeftSidebar = () => {
  const { sidebarType, setSidebarType } = useSidebar();
  const { account } = useAccount();
  const [showSkeleton, setShowSkeleton] = useState(false);

  const handleCRMClick = async () => {
    try {
      const res = await getSsoToken();
      if (res.status === "success") {
        const token = res.data.sso_token;
        const crmUrl = import.meta.env.VITE_CRM_URL || "http://localhost:3000";
        window.open(`${crmUrl}/sso?token=${token}`, "_blank");
      }
    } catch (error) {
      console.error("Failed to get SSO token", error);
      // Fallback or error notification
    }
  };

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 800);

    // Cleanup para evitar race condition se componente for desmontado
    return () => {
      clearTimeout(timer);
      setShowSkeleton(false);
    };
  }, []);

  if (showSkeleton) {
    return (
      <Container>
        {Array.from({ length: 6 }).map((_, index) => (
          <Box
            key={`left-sidebar-skeleton-${index}`}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: { xs: "44px", sm: "48px" },
              mb: 1,
            }}
          >
            <Skeleton
              variant="circular"
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
              }}
            />
          </Box>
        ))}
      </Container>
    );
  }

  // Verifica se o usuário é administrador ou coordenador
  const isAdmin = account?.role === "ADMINISTRATOR";
  const isCoordinator = account?.role === "COORDINATOR";
  const canAccessLeads = isAdmin || isCoordinator;

  return (
    <Container
      sx={{
        "@media (max-width: 600px)": {
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 999,
          backgroundColor: "#f0f2f5",
          borderRight: "1px solid #e9edef",
        },
      }}
    >
      <SidebarNavButton
        icon={ChatIcon}
        tooltip="Conversas"
        active={sidebarType === "conversations"}
        onClick={() => {
          if (sidebarType === "conversations") {
            // Se já está aberto, oculta
            setSidebarType(null);
          } else {
            // Se não está aberto, abre
            setSidebarType("conversations");
          }
        }}
      />

      {/* Botão de Contatos de Leads - para administradores e coordenadores */}
      {canAccessLeads && (
        <SidebarNavButton
          icon={ContactPhoneIcon}
          tooltip="Contatos de Leads"
          active={sidebarType === "lead_contacts"}
          onClick={() => {
            if (sidebarType === "lead_contacts") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("lead_contacts");
            }
          }}
        />
      )}

      {/* Botão de Templates - apenas para administradores */}
      {isAdmin && (
        <SidebarNavButton
          icon={TemplateIcon}
          tooltip="Templates do WhatsApp"
          active={sidebarType === "templates"}
          onClick={() => {
            if (sidebarType === "templates") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("templates");
            }
          }}
        />
      )}

      {/* Botão de Envio em Massa Integrado - apenas para administradores */}
      {isAdmin && (
        <SidebarNavButton
          icon={CampaignIcon}
          tooltip="Envio em Massa"
          active={sidebarType === "bulk_send_manager"}
          onClick={() => {
            if (sidebarType === "bulk_send_manager") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("bulk_send_manager");
            }
          }}
        />
      )}

      {/* Botão de Botões Iterativos - apenas para administradores */}
      {isAdmin && (
        <SidebarNavButton
          icon={TouchAppIcon}
          tooltip="Botões Iterativos"
          active={sidebarType === "phrase_config_manager"}
          onClick={() => {
            if (sidebarType === "phrase_config_manager") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("phrase_config_manager");
            }
          }}
        />
      )}

      {/* Botão de Automações - apenas para administradores */}
      {isAdmin && (
        <SidebarNavButton
          icon={AutoAwesomeIcon}
          tooltip="Automações"
          active={sidebarType === "automation_manager"}
          onClick={() => {
            if (sidebarType === "automation_manager") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("automation_manager");
            }
          }}
        />
      )}

      {/* Botão de Controle de IA - apenas para administradores */}
      {isAdmin && (
        <SidebarNavButton
          icon={PsychologyIcon}
          tooltip="Controle de IA"
          active={sidebarType === "ai_control_manager"}
          onClick={() => {
            if (sidebarType === "ai_control_manager") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("ai_control_manager");
            }
          }}
        />
      )}

      {/* Botão Acessar CRM — apenas para administradores ou quem tem a flag habilitada */}
      {(isAdmin || !!account?.company?.crm?.company_id) && (
        <Tooltip title="Acessar CRM (Pipeline)" placement="right">
          <StyledIconButton
            onClick={handleCRMClick}
            data-active={false}
            sx={{
              color: "#008069",
              background:
                "linear-gradient(135deg, rgba(0,128,105,0.1) 0%, rgba(0,128,105,0.05) 100%)",
              border: "1px solid rgba(0,128,105,0.2)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(0,128,105,0.2) 0%, rgba(0,128,105,0.15) 100%)",
                transform: "scale(1.08)",
              },
            }}
          >
            <DashboardIcon />
          </StyledIconButton>
        </Tooltip>
      )}

      <Box
        sx={{
          mt: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          "@media (max-width: 600px)": {
            mt: "auto", // Manter margin-top auto em mobile para ficar na extremidade inferior
            mb: 1, // Adicionar margin-bottom para espaçamento
            gap: "8px", // Aumentar gap em mobile
          },
        }}
      >
        {isAdmin && (
          <SidebarNavButton
            icon={SettingsApplicationsIcon}
            tooltip="Configurações"
            active={sidebarType === "settings"}
            onClick={() => {
              if (sidebarType === "settings") {
                setSidebarType(null);
              } else {
                setSidebarType("settings");
              }
            }}
          />
        )}
        <SidebarNavButton
          icon={BusinessIcon}
          tooltip="Perfil da Empresa"
          active={sidebarType === "profile"}
          onClick={() => {
            if (sidebarType === "profile") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("profile");
            }
          }}
        />
        <SidebarNavButton
          icon={PersonIcon}
          tooltip="Perfil do Usuário"
          active={sidebarType === "user_profile"}
          onClick={() => {
            if (sidebarType === "user_profile") {
              // Se já está aberto, oculta
              setSidebarType(null);
            } else {
              // Se não está aberto, abre
              setSidebarType("user_profile");
            }
          }}
        />
      </Box>
    </Container>
  );
};

export default LeftSidebar;
